import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { X, Inbox, XCircle, Image as ImageIcon, ArrowRight } from 'lucide-react';
import { Album, AlbumMedia } from '../types';
import { apiService } from '../services';
import { HeroBackground, PageHeader } from '../components';

interface MediaModalProps {
  media: AlbumMedia | null;
  onClose: () => void;
}

const MediaModal: React.FC<MediaModalProps> = ({ media, onClose }) => {
  const { t } = useTranslation();

  if (!media) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const mediaUrl = apiService.getAlbumMediaUrl(media.id);

  return (
    <div
      className='fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4'
      onClick={handleBackdropClick}
    >
      <div className='relative max-w-4xl max-h-full rounded-lg overflow-hidden'>
        <button
          onClick={onClose}
          className='absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center hover:bg-opacity-75 z-10'
        >
          <X className='w-4 h-4' />
        </button>

        {media.file_type === 'image' ? (
          <img
            src={mediaUrl}
            alt={media.title || media.filename}
            className='max-w-full max-h-[80vh] object-contain'
          />
        ) : (
          <video
            src={mediaUrl}
            controls
            className='max-w-full max-h-[80vh]'
            autoPlay
          >
            {t('albums.browser_no_video')}
          </video>
        )}

        {(media.title || media.description) && (
          <div className='p-4 bg-white'>
            {media.title && (
              <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                {media.title}
              </h3>
            )}
            {media.description && (
              <p className='text-gray-600 text-sm'>{media.description}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

interface AlbumCardProps {
  album: Album;
  onMediaClick: (media: AlbumMedia) => void;
}

const AlbumCard: React.FC<AlbumCardProps> = ({ album, onMediaClick }) => {
  const { t } = useTranslation();
  const [mediaItems, setMediaItems] = useState<AlbumMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [brokenImages, setBrokenImages] = useState<Set<number>>(new Set());

  useEffect(() => {
    const fetchAlbumMedia = async () => {
      try {
        const response = await apiService.getAlbumMedia(album.id);
        setMediaItems(response.media_items);
      } catch (error) {
        console.error('Failed to fetch album media:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlbumMedia();
  }, [album.id]);

  const handleImageError = (mediaId: number) => {
    setBrokenImages((prev) => new Set(prev).add(mediaId));
  };

  // Calculate column count based on screen size
  const getColumnCount = () => {
    if (typeof window === 'undefined') return 3;
    const width = window.innerWidth;
    if (width < 640) return 1; // sm
    if (width < 1024) return 2; // md
    if (width < 1280) return 3; // lg
    return 4; // xl
  };

  const [columnCount, setColumnCount] = useState(getColumnCount());

  useEffect(() => {
    const handleResize = () => {
      setColumnCount(getColumnCount());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className='bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 p-4'>
      {/* Masonry Layout with Album Info */}
      <div
        className='gap-2'
        style={{
          columnCount: columnCount,
          columnGap: '0.5rem',
        }}
      >
        {/* Album Information Card - First Item */}
        <div className='break-inside-avoid mb-2 bg-gray-50 rounded-lg p-4 border border-gray-200'>
          <div className='flex items-start justify-between mb-2'>
            <h2 className='text-xl font-semibold text-gray-900 pr-2'>{album.name}</h2>
            <Link
              to={`/pilgrimages/${album.id}`}
              className='text-church-brown hover:text-church-dark text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 inline-flex items-center gap-1'
            >
              {t('albums.view_album')} <ArrowRight className='w-3.5 h-3.5' />
            </Link>
          </div>
          {album.description && (
            <p className='text-gray-600 text-sm mt-1 mb-3'>{album.description}</p>
          )}
          <div className='flex flex-col gap-2 text-xs text-gray-500'>
            <span>{t('albums.media_count', { count: album.media_count })}</span>
            {album.author && (
              <span>
                {t('albums.by_author', {
                  name: album.author.full_name || album.author.username,
                })}
              </span>
            )}
            {album.date && (
              <span>{new Date(album.date).toLocaleDateString('fr-FR')}</span>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className='break-inside-avoid mb-2 flex justify-center items-center py-16 min-h-[300px]'>
            <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'></div>
          </div>
        )}

        {/* Empty State */}
        {!loading && mediaItems.length === 0 && (
          <div className='break-inside-avoid mb-2 flex justify-center items-center py-16 min-h-[300px] text-gray-500'>
            <div className='text-center'>
              <Inbox className='w-10 h-10 mx-auto mb-2 text-gray-300' />
              <p>{t('albums.empty_media')}</p>
            </div>
          </div>
        )}

        {/* Media Items */}
        {!loading &&
          mediaItems.map((media) => {
            const mediaUrl = apiService.getAlbumMediaUrl(media.id);

            return (
              <div key={media.id} className='break-inside-avoid mb-2'>
                {media.file_type === 'image' ? (
                  brokenImages.has(media.id) ? (
                    <div
                      className='w-full flex border flex-col items-center justify-center cursor-pointer p-4 border-gray-300 bg-gray-50 rounded-lg'
                      onClick={() => onMediaClick(media)}
                    >
                      <div className='text-gray-400 mb-2'>
                        <svg className='w-8 h-8' fill='currentColor' viewBox='0 0 24 24'>
                          <path d='M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z' />
                        </svg>
                      </div>
                      <div className='text-gray-500 text-xs text-center'>
                        <div>{t('albums.image_unavailable')}</div>
                        <div className='text-gray-400'>
                          {t('albums.image_name', { name: media.filename })}
                        </div>
                        <div className='text-gray-400'>
                          {t('albums.image_id', { id: media.id })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div
                      className='relative cursor-pointer rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-church-gold/50'
                      onClick={() => onMediaClick(media)}
                    >
                      <img
                        src={mediaUrl}
                        alt={media.title || media.filename}
                        className='w-full h-auto object-contain'
                        loading='lazy'
                        onError={() => handleImageError(media.id)}
                      />
                      {(media.title || media.description) && (
                        <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent p-3'>
                          {media.title && (
                            <h4 className='text-sm font-medium text-white mb-1'>
                              {media.title}
                            </h4>
                          )}
                          {media.description && (
                            <p className='text-xs text-white/90 line-clamp-2'>
                              {media.description}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )
                ) : (
                  <div
                    className='w-full bg-gray-900 flex items-center justify-center cursor-pointer hover:bg-gray-800 transition-colors relative rounded-lg border border-gray-200 min-h-[200px]'
                    onClick={() => onMediaClick(media)}
                  >
                    <div className='text-white text-center p-4'>
                      <div className='text-4xl mb-2'>▶️</div>
                      <p className='text-sm'>{media.title || t('albums.video')}</p>
                      {media.description && (
                        <p className='text-xs text-white/70 mt-1'>{media.description}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export const AlbumsPage: React.FC = () => {
  const { t } = useTranslation();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<AlbumMedia | null>(null);

  useEffect(() => {
    const fetchAlbums = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getPublishedAlbums();
        setAlbums(response.albums);
      } catch (err) {
        console.error('Failed to fetch albums:', err);
        setError(t('albums.error_load'));
      } finally {
        setLoading(false);
      }
    };

    fetchAlbums();
  }, []);

  const handleMediaClick = (media: AlbumMedia) => {
    setSelectedMedia(media);
  };

  const closeModal = () => {
    setSelectedMedia(null);
  };

  if (loading) {
    return (
      <div className='min-h-screen  flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4'></div>
          <p className='text-gray-600'>{t('albums.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <XCircle className='w-10 h-10 mx-auto mb-4 text-red-400' />
          <h2 className='text-xl font-semibold text-gray-900 mb-2'>
            {t('albums.error_title')}
          </h2>
          <p className='text-gray-600'>{error}</p>
          <button
            onClick={() => window.location.reload()}
            className='mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
          >
            {t('albums.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen'>
      {/* Hero header cropped to 1/3 */}
      <HeroBackground size='third' />
      
      {/* Page Title */}
      <PageHeader
        title={t('nav.pilgrimages')}
        subtitle={t('subtitles.pilgrimages')}
      />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>

        {/* Albums Grid */}
        {albums.length === 0 ? (
          <div className='text-center py-16'>
            <ImageIcon className='w-16 h-16 mx-auto mb-4 text-gray-300' />
            <h2 className='text-2xl font-semibold text-gray-900 mb-2'>
              {t('albums.no_albums_title')}
            </h2>
            <p className='text-gray-600'>
              {t('albums.no_albums_text')}
            </p>
          </div>
        ) : (
          <div className='space-y-8'>
            {albums.map((album) => (
              <AlbumCard
                key={album.id}
                album={album}
                onMediaClick={handleMediaClick}
              />
            ))}
          </div>
        )}
      </div>

      {/* Media Modal */}
      <MediaModal media={selectedMedia} onClose={closeModal} />
    </div>
  );
};
